import { useState, useRef, useCallback } from 'react';
import { Mic, MicOff, Loader2, X, CheckCircle2, RotateCcw } from 'lucide-react';
import axios from 'axios';
import { API_URL } from '../lib/api';
import { toast } from 'sonner';

export default function VoiceInput({ onResult }) {
    const [listening, setListening] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [transcript, setTranscript] = useState('');
    const [showOverlay, setShowOverlay] = useState(false);
    const [result, setResult] = useState(null); // {fieldCount, detectedFields}
    const recognitionRef = useRef(null);
    const finalTranscriptRef = useRef('');

    const startListening = useCallback(() => {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SpeechRecognition) {
            toast.error('Saisie vocale non supportée sur ce navigateur');
            return;
        }

        const recognition = new SpeechRecognition();
        recognition.lang = 'fr-FR';
        recognition.continuous = true;
        recognition.interimResults = true;
        recognitionRef.current = recognition;

        setShowOverlay(true);
        setTranscript('');
        setResult(null);
        setListening(true);
        finalTranscriptRef.current = '';

        recognition.onresult = (event) => {
            let interim = '';
            for (let i = event.resultIndex; i < event.results.length; i++) {
                const t = event.results[i][0].transcript;
                if (event.results[i].isFinal) {
                    finalTranscriptRef.current += t + ' ';
                } else {
                    interim = t;
                }
            }
            setTranscript(finalTranscriptRef.current + interim);
        };

        recognition.onerror = (event) => {
            if (event.error !== 'aborted') {
                toast.error('Erreur micro : ' + event.error);
            }
            setListening(false);
        };

        recognition.onend = () => {
            setListening(false);
        };

        recognition.start();
    }, []);

    const stopAndProcess = useCallback(async () => {
        if (recognitionRef.current) {
            recognitionRef.current.stop();
        }
        setListening(false);

        const text = transcript.trim();
        if (!text) {
            setShowOverlay(false);
            return;
        }

        setProcessing(true);
        try {
            const res = await axios.post(`${API_URL}/api/voice-parse`, { text }, { withCredentials: true });
            if (res.data.success && res.data.data) {
                const count = res.data.field_count || 0;
                const fields = res.data.detected_fields || [];
                onResult(res.data.data, fields);
                setResult({ fieldCount: count, detectedFields: fields });
            } else {
                toast.error(res.data.error || 'Impossible de comprendre');
                setShowOverlay(false);
            }
        } catch (e) {
            toast.error('Erreur lors de l\'analyse');
            setShowOverlay(false);
        } finally {
            setProcessing(false);
        }
    }, [transcript, onResult]);

    const redictate = useCallback(() => {
        setResult(null);
        setTranscript('');
        finalTranscriptRef.current = '';
        startListening();
    }, [startListening]);

    const close = useCallback(() => {
        if (recognitionRef.current) {
            recognitionRef.current.stop();
        }
        setListening(false);
        setProcessing(false);
        setShowOverlay(false);
        setTranscript('');
        setResult(null);
    }, []);

    const FIELD_LABELS = {
        nom: 'Nom véhicule',
        modele: 'Modèle',
        immatriculation: 'Immatriculation',
        prix_achat: 'Prix d\'achat',
        prix_vente: 'Prix de vente',
        frais_circulation: 'Carte grise',
        remise_client: 'Remise client',
        frais: 'Frais additionnels',
    };

    return (
        <>
            {/* Mic trigger button */}
            <button
                onClick={startListening}
                className="w-full bg-gray-900 text-white rounded-2xl p-4 flex items-center justify-center gap-2.5 active:scale-[0.98] transition-all shadow-sm"
                data-testid="voice-input-btn"
                type="button"
            >
                <Mic className="w-5 h-5" />
                <span className="text-[14px] font-medium">Saisie vocale</span>
            </button>

            {/* Fullscreen overlay */}
            {showOverlay && (
                <div className="fixed inset-0 z-[100] bg-gray-900/95 backdrop-blur-sm flex flex-col items-center justify-center px-6" data-testid="voice-overlay">
                    {/* Close */}
                    <button onClick={close} className="absolute top-[calc(1rem+env(safe-area-inset-top))] right-4 p-2 text-gray-400 hover:text-white" type="button">
                        <X className="w-6 h-6" />
                    </button>

                    {/* === RESULT VIEW === */}
                    {result ? (
                        <div className="w-full max-w-sm animate-fade-in" data-testid="voice-result">
                            <div className="flex flex-col items-center mb-6">
                                <div className="w-16 h-16 rounded-full bg-emerald-500 flex items-center justify-center mb-4 shadow-lg shadow-emerald-500/20">
                                    <CheckCircle2 className="w-8 h-8 text-white" />
                                </div>
                                <p className="text-white text-lg font-semibold text-center" data-testid="voice-result-count">
                                    {result.fieldCount} information{result.fieldCount > 1 ? 's' : ''} reconnue{result.fieldCount > 1 ? 's' : ''}
                                </p>
                                <p className="text-gray-400 text-sm mt-1 text-center">Vérifiez les champs surlignés</p>
                            </div>

                            {/* Detected fields list */}
                            <div className="bg-white/10 rounded-xl p-4 mb-6 space-y-1.5">
                                {result.detectedFields.map(f => (
                                    <div key={f} className="flex items-center gap-2">
                                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                                        <span className="text-[13px] text-gray-200">{FIELD_LABELS[f] || f}</span>
                                    </div>
                                ))}
                            </div>

                            {/* Action buttons */}
                            <div className="space-y-3">
                                <button
                                    onClick={close}
                                    className="w-full bg-white text-gray-900 rounded-2xl py-3.5 text-[15px] font-semibold active:scale-[0.98] transition-transform"
                                    data-testid="voice-result-close"
                                    type="button"
                                >
                                    Vérifier et compléter
                                </button>
                                <button
                                    onClick={redictate}
                                    className="w-full bg-white/10 text-white rounded-2xl py-3.5 text-[14px] font-medium flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
                                    data-testid="voice-redictate-btn"
                                    type="button"
                                >
                                    <RotateCcw className="w-4 h-4" />
                                    Refaire la dictée
                                </button>
                            </div>
                        </div>
                    ) : (
                        /* === LISTENING / PROCESSING VIEW === */
                        <>
                            <div className={`relative mb-8 ${listening ? 'animate-pulse' : ''}`}>
                                <div className={`w-24 h-24 rounded-full flex items-center justify-center ${listening ? 'bg-red-500 shadow-lg shadow-red-500/30' : processing ? 'bg-[#6366F1]' : 'bg-gray-700'} transition-colors`}>
                                    {processing ? (
                                        <Loader2 className="w-10 h-10 text-white animate-spin" />
                                    ) : listening ? (
                                        <Mic className="w-10 h-10 text-white" />
                                    ) : (
                                        <MicOff className="w-10 h-10 text-gray-400" />
                                    )}
                                </div>
                                {listening && (
                                    <>
                                        <div className="absolute inset-0 rounded-full border-2 border-red-400 animate-ping opacity-30" />
                                        <div className="absolute inset-[-8px] rounded-full border border-red-400/20 animate-ping opacity-20" style={{ animationDelay: '0.3s' }} />
                                    </>
                                )}
                            </div>

                            <p className="text-white text-lg font-medium mb-2">
                                {processing ? 'Analyse en cours...' : listening ? 'Parlez maintenant...' : 'Prêt'}
                            </p>
                            <p className="text-gray-400 text-sm mb-6 text-center max-w-sm">
                                {listening ? 'Décrivez le véhicule : marque, modèle, prix d\'achat, prix de vente, frais...' : processing ? 'L\'IA analyse votre description' : ''}
                            </p>

                            {transcript && (
                                <div className="bg-white/10 rounded-xl p-4 max-w-sm w-full mb-8 max-h-32 overflow-y-auto" data-testid="voice-transcript">
                                    <p className="text-white text-[14px] leading-relaxed">{transcript}</p>
                                </div>
                            )}

                            {listening && (
                                <button
                                    onClick={stopAndProcess}
                                    className="bg-white text-gray-900 rounded-full px-8 py-3.5 text-[15px] font-semibold active:scale-95 transition-transform shadow-lg"
                                    data-testid="voice-stop-btn"
                                    type="button"
                                >
                                    Valider et analyser
                                </button>
                            )}
                        </>
                    )}
                </div>
            )}
        </>
    );
}
